import { useState } from 'react';

import GroupAddIcon from '@mui/icons-material/GroupAdd';
import GroupRemoveIcon from '@mui/icons-material/GroupRemove';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { alpha, Box, Card, CardContent, Grid, Typography } from '@mui/material';
import AnimatedBackButton from 'components/elements/AnimatedBackButton';
import AnimatedCard from 'components/elements/AnimatedCard';
import HeaderComp from 'components/elements/HeadingComponent';
import { useNavigate } from 'react-router-dom';

import AddStaffToGroup from './AddStaffToGroup';
import CreateStaffGroup from './CreateStaffGroup';
import DeleteStaffGroup from './DeleteStaffGroup';
import DeleteStaffGroupMember from './DeleteStaffGroupMember';

const StaffGroups = () => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const navigate = useNavigate();

  const staffGroupOptions = [
    {
      id: 'create-group',
      title: 'Create staff group',
      description: 'Create a new staff group with custom permissions',
      icon: GroupAddIcon,
      onClick: () => setSelectedOption('Create staff group'),
      type: 'primary',
      color: '#6366f1', // Indigo
      bgColor: alpha('#6366f1', 0.08),
      borderColor: alpha('#6366f1', 0.2),
    },
    {
      id: 'add-staff',
      title: 'Add staff to group',
      description: 'Add existing staff members to a group',
      icon: PersonAddIcon,
      onClick: () => setSelectedOption('Add staff to group'),
      type: 'primary',
      color: '#059669', // Emerald
      bgColor: alpha('#059669', 0.08),
      borderColor: alpha('#059669', 0.2),
    },
    {
      id: 'delete-group',
      title: 'Delete staff group',
      description: 'Remove an entire staff group and its members',
      icon: GroupRemoveIcon,
      onClick: () => setSelectedOption('Delete staff group'),
      type: 'error',
      color: '#dc2626', // Red
      bgColor: alpha('#dc2626', 0.08),
      borderColor: alpha('#dc2626', 0.2),
    },
    {
      id: 'delete-member',
      title: 'Delete staff group member',
      description: 'Remove specific members from a staff group',
      icon: GroupRemoveIcon,
      onClick: () => setSelectedOption('Delete staff group member'),
      type: 'error',
      color: '#ea580c', // Orange
      bgColor: alpha('#ea580c', 0.08),
      borderColor: alpha('#ea580c', 0.2),
    },
  ];

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <>
      {selectedOption === 'Create staff group' && (
        <>
          {/* <Box
            component={Button}
            display="flex"
            alignItems="center"
            gap={1}
            mb={2}
            onClick={() => setSelectedOption(null)}
          >
            <FirstPageIcon color="primary" />
            <Typography variant="subtitle1" color="primary">
              Go back
            </Typography>
          </Box> */}
          <CreateStaffGroup />
        </>
      )}
      {selectedOption === 'Add staff to group' && (
        <>
          <AddStaffToGroup />
        </>
      )}
      {selectedOption === 'Delete staff group' && (
        <>
          <DeleteStaffGroup />
        </>
      )}
      {selectedOption === 'Delete staff group member' && (
        <>
          <DeleteStaffGroupMember />
        </>
      )}
      {selectedOption === null ? (
        <Box>
          <HeaderComp>Staff groups</HeaderComp>

          <AnimatedBackButton onClick={handleBackClick} />

          {/* Modern Staff Group Options Grid */}
          <Box mb={4}>
            <Grid container spacing={3}>
              {staffGroupOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <Grid item xs={12} sm={6} md={3} key={option.id}>
                    <AnimatedCard
                      title={option.title}
                      description={option.description}
                      icon={<Icon />}
                      color={option.color}
                      bgColor={option.bgColor}
                      borderColor={option.borderColor}
                      isSelected={selectedOption === option.title}
                      onClick={option.onClick}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* Quick Tips Section */}
          <Card
            sx={{
              background: '#fbfcfc',
              border: '1px solid',
              borderColor: 'rgba(0, 128, 128, 0.06)',
              borderRadius: 1.5,
              mb: 4,
              boxShadow: 'none',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'rgba(0, 128, 128, 0.15)',
                bgcolor: 'rgba(255, 255, 255, 0.6)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.03)',
              },
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box
                  sx={{
                    bgcolor: 'primary.main',
                    p: 0.75,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <GroupAddIcon sx={{ fontSize: 18, color: 'white' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 500,
                      mb: 1,
                      color: 'text.primary',
                      fontSize: '0.95rem',
                    }}
                  >
                    Staff Group Management
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.5,
                      fontSize: '0.8rem',
                    }}
                  >
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      <Box component="li" sx={{ mb: 0.5 }}>
                        <strong style={{ color: 'text.primary' }}>
                          Create groups
                        </strong>{' '}
                        to organize staff members with similar roles and
                        permissions.
                      </Box>
                      <Box component="li" sx={{ mb: 0.5 }}>
                        <strong style={{ color: 'text.primary' }}>
                          Add members
                        </strong>{' '}
                        to existing groups to grant them specific access rights.
                      </Box>
                      <Box component="li">
                        <strong style={{ color: 'text.primary' }}>
                          Remove groups or members
                        </strong>{' '}
                        when access is no longer needed or roles change.
                      </Box>
                    </Box>
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      ) : null}
    </>
  );
};

export default StaffGroups;
